import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CirclePlus, File, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";
import {
    getFiles,
    uploadFile,
    deleteFile,
} from "@/api/client/communications";
import {createComment, deleteComment, getCommunicationById, updateCommunication} from "@/api/edit-comunications/edit-comunications.js";
import {useStore} from "@/store/index.js";
import DOMPurify from "dompurify";
import { getFileDownloadUrl } from "@/services/files";

const FileUploadAndComments = ({ viewType, isAdmin, communication: propCommunication }) => {
    const params = useParams();
    const [isSubmitting, setIsSubmitting] = useState(false); // Nuevo estado para el botón
    const [communication, setCommunication] = useState(propCommunication || null);
    const [comments, setComments] = useState([]);
    const [files, setFiles] = useState([]);
    const [isChangingState, setIsChangingState] = useState(false);
    const { t } = useTranslation();
    const loggedInUser = useStore((state) => state.loggedInUser);
    const states = useStore((state) => state.states || []);
    const getStates = useStore((state) => state.getStates);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: "",
            text: "",
        },
    });

    // Cargar la comunicación si no se proporciona como prop
    useEffect(() => {
        if (!propCommunication) {
            const fetchCommunication = async () => {
                try {
                    const response = await getCommunicationById(params.id);
                    setCommunication(response);
                } catch (error) {
                    console.error("Error al cargar la comunicación:", error);
                }
            };
            fetchCommunication();
        }
    }, [propCommunication, params.id]);

    useEffect(() => {
        loadFiles();
        loadComments();
    }, []);

    useEffect(() => {
        // Cargar estados si no están disponibles
        if (states.length === 0) {
            getStates();
        }
    }, [states.length, getStates]);

    const transformComments = (rawComments) => {
        if (!rawComments) {
            return [];
        }

        if (Array.isArray(rawComments)) {
            return rawComments;
        }

        return Object.values(rawComments);
    };

    const getCommentsForView = (rawComments) => {
        const commentList = transformComments(rawComments);

        if (viewType === "public") {
            return commentList.filter((comment) => comment?.public === true);
        }

        return commentList.filter((comment) => comment?.public === false);
    };

    const loadFiles = async () => {
        try {
            const response = await getCommunicationById(params.id);
            setFiles(response.files || []); // Asegura que `files` sea un array incluso si está vacío
        } catch (error) {
            console.error("Error al cargar archivos:", error);
        }
    };


    const loadComments = async () => {
        try {
            const response = await getCommunicationById(params.id);
            const filteredComments = getCommentsForView(response.comments);
            setComments(filteredComments);
        } catch (error) {
            console.error("Error al cargar comentarios:", error);
        }
    };


    useEffect(() => {
        getCommunicationById(params.id).then((response) => {
            const filteredComments = getCommentsForView(response.comments);
            setComments(filteredComments);
        })
    }, [viewType, communication])

    const handleFileUpload = async (file, type) => {
        const isPublic = viewType === "public" ? "public" : "";
        try {
            await uploadFile(file, communication.id, isPublic, type);
            await loadFiles(); // Recargar archivos tras el éxito
            toast.success(t("alertDialog.toast.fileUploadSuccess"));
        } catch (error) {
            console.error("Error al subir el archivo:", error);
            toast.error(t("alertDialog.toast.fileUploadError"));
        }
    };


    const handleDeleteFileUploaded = async (id, communicationId) => {
        try {
            await deleteFile(id, communicationId);
            loadFiles(); // Recargar archivos tras el éxito
            toast.success(t("alertDialog.toast.fileDeleteSuccess"));
        } catch (error) {
            console.error("Error al eliminar el archivo:", error);
            toast.error(t("alertDialog.toast.fileDeleteError"));
        }
    };


    const handleDeleteComment = async (id) => {
        try {
            await deleteComment(id);
            setComments((prevComments) => prevComments.filter((c) => c.id !== id));
            toast.success(t("alertDialog.toast.commentDeleteSuccess"));
        } catch (error) {
            console.error("Error al eliminar el comentario:", error);
            toast.error(t("alertDialog.toast.commentDeleteError"));
        }
    };


    const onSubmit = async (data) => {
        const type = viewType === "public";
        const commentSent = {
            ...data,
            public: type,
            communication: { id: communication.id },
            userId: loggedInUser?.id || null
        };
        setIsSubmitting(true);
        try {
            const newComment = await createComment(commentSent);
            setComments((prevComments) => [...prevComments, newComment]);
            toast.success(t("alertDialog.toast.commentCreateSuccess"));
            reset();
        } catch (error) {
            console.error("Error al crear el comentario:", error);
            toast.error(t("alertDialog.toast.commentCreateError"));
        } finally {
            setIsSubmitting(false);
        }
    };
    const filterFiles = (type) => {
        const validFiles = Array.isArray(files) ? files : [];

        const filteredFiles = validFiles.filter(
            (file) => file.public === (viewType === "public") && file.type === type,
        );

        return filteredFiles;
    };


    const truncateFileName = (name, maxLength = 80) => {
        if (name.length > maxLength) {
            return `${name.substring(0, maxLength)}...`;
        }
        return name;
    };

    const getCommunicationReference = () => {
        if (params.id) {
            return params.id;
        }

        return String(communication?.id || propCommunication?.id || "");
    };

    const handleFileDownload = async (event, file) => {
        event.preventDefault();

        // Abrimos la pestaña durante el gesto del usuario para que el navegador no la bloquee
        // mientras obtenemos una URL firmada nueva.
        const downloadWindow = window.open("", "_blank");
        if (downloadWindow) {
            downloadWindow.opener = null;
        }

        try {
            const refreshedCommunication = await getCommunicationById(getCommunicationReference());
            const refreshedFile = refreshedCommunication?.files?.find(
                (candidate) => candidate.id === file.id,
            );
            const downloadUrl = getFileDownloadUrl(refreshedFile?.path || refreshedFile?.url);

            if (!downloadUrl) {
                throw new Error(`File ${file.id} is not available for download.`);
            }

            if (downloadWindow) {
                downloadWindow.location.href = downloadUrl;
            } else {
                window.open(downloadUrl, "_blank", "noopener,noreferrer");
            }
        } catch (error) {
            if (downloadWindow) {
                downloadWindow.close();
            }
            console.error("Error al obtener una URL de descarga actualizada:", error);
            toast.error(t("alertDialog.toast.fileUploadError"));
        }
    };

    function formatDate(isoDate) {
        const date = new Date(isoDate)

        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0") // getMonth() devuelve 0-11
        const year = date.getFullYear()

        const hours = String(date.getHours()).padStart(2, "0")
        const minutes = String(date.getMinutes()).padStart(2, "0")

        return `${day}/${month}/${year} ${hours}:${minutes}`
    }

    const renderFiles = (files, isAdmin) => {
        return files && !files.length ? (
            <div className="text-slate-500 ml-2">
                {t("edit.fileUploadAndComments.noFiles")}
            </div>
        ) : (
            files
                ?.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
                .map((file) => (
                    <div className="flex mt-2" key={file.id}>
          <span className="flex items-center text-slate-500 ml-2">
            <a
              href="#"
              onClick={(event) => handleFileDownload(event, file)}
            >
              <span className="font-semibold">
                {truncateFileName(file.filename)}
              </span>{" "}
                - {formatDate(file.uploadedAt)}
            </a>
          </span>
                        {isAdmin && loggedInUser?.roles?.includes("superadmin") && (
                            <span>
              <div className="flex items-center justify-end">
                <Button
                    className="ml-1 h-2/4 bg-transparent rounded-full hover:bg-red-300"
                    onClick={() => handleDeleteFileUploaded(file.id, communication.id)}
                >
                  <Trash className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </span>
                        )}
                    </div>
                ))
        );
    };


    const filteredFilesAdmin = filterFiles("admin")
    const filteredFilesFollowUp = filterFiles("followUp")
    const filteredFilesCommunication =
        filteredFilesFollowUp && filteredFilesFollowUp.length
            ? [...filteredFilesFollowUp, ...filterFiles("communication")]
            : filterFiles("communication")

    const handleStateChange = async (newStateId) => {
        if (!communication || isChangingState) return;
        
        try {
            setIsChangingState(true);
            await updateCommunication(communication.id, { state_id: newStateId });
            
            // Recargar la comunicación para obtener el estado actualizado
            const updatedCommunication = await getCommunicationById(communication.id);
            setCommunication(updatedCommunication);
            
            toast.success(t("alertDialog.toast.updatedSuccess"));
        } catch (error) {
            console.error("Error al cambiar el estado:", error);
            toast.error(t("alertDialog.toast.updatedError"));
        } finally {
            setIsChangingState(false);
        }
    };

    return (
        <div className="border border-dashed rounded-lg p-6 border-primary animate-fade-in">
            <Toaster/>
            {viewType == "public" && (
                <Card className="mb-4">
                    <CardHeader>
                        <div className="flex p-3 items-center">
            <span className="flex w-5 h-5 mr-1 items-center">
              <File/>
            </span>
                            <span className="flex items-center mr-2">
              {t("edit.fileUploadAndComments.communicationFilesTitle")}
            </span>
                            <span>
              <div className="flex">
                <label className="bg-transparent rounded-full cursor-pointer">
                  <CirclePlus className="w-5 h-5 text-primary"/>
                  <input
                      type="file"
                      className="hidden"
                      onChange={(e) =>
                          handleFileUpload(e.target.files[0], "followUp")
                      }
                  />
                </label>
              </div>
            </span>
                        </div>
                    </CardHeader>
                    <hr/>
                    <CardContent>
                        {renderFiles(filteredFilesCommunication, isAdmin)}
                    </CardContent>
                </Card>
            )}
            {/* <Card className="mb-4">
        <CardHeader>
          <div className="flex p-3 items-center">
            <span className="flex w-5 h-5 mr-1 items-center">
              <File />
            </span>
            <span className="flex items-center mr-2">
              {t("edit.fileUploadAndComments.followUpFilesTitle")}
            </span>
            <span>
              <div className="flex">
                <label className="bg-transparent rounded-full cursor-pointer">
                  <CirclePlus className="w-5 h-5 text-primary" />
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) =>
                      handleFileUpload(e.target.files[0], "followUp")
                    }
                  />
                </label>
              </div>
            </span>
          </div>
        </CardHeader>
        <hr />
        <CardContent>{renderFiles(filteredFilesFollowUp)}</CardContent>
      </Card> */}

            <Card className="mb-4">
                <CardHeader>
                    <div className="flex p-3 items-center">
            <span className="flex w-5 h-5 mr-1 items-center">
              <File/>
            </span>
                        <span className="flex items-center mr-2">
              {t("edit.fileUploadAndComments.adminFilesTitle")}
            </span>
                        {isAdmin && (
                            <span>
              <div className="flex">
                <label className="bg-transparent rounded-full cursor-pointer">
                  <CirclePlus className="w-5 h-5 text-primary"/>
                  <input
                      type="file"
                      className="hidden"
                      onChange={(e) =>
                          handleFileUpload(e.target.files[0], "admin")
                      }
                  />
                </label>
              </div>
            </span>
                        )}
                    </div>
                </CardHeader>
                <hr/>
                <CardContent>{renderFiles(filteredFilesAdmin, isAdmin)}</CardContent>
            </Card>

            <div className="mb-9">
                <h3>{t("edit.fileUploadAndComments.commentsHeader")}</h3>
                {comments && comments.length ? (
                    <div className="border border-dashed rounded-lg p-6 border-primary animate-fade-in">
                        {comments?.map((comment, index) => {
                            const sanitizedCommentText = DOMPurify.sanitize(comment?.text || "");

                            return (
                                <Card key={index} className="mb-2">
                                    <CardHeader className="ml-1">
                                        <div className="flex justify-between">
                                            <div>
                                                {comment.user?.name || comment.user?.email}
                                                {comment.public ? (
                                                    <span className="flex text-green-500">
                          ({t("edit.fileUploadAndComments.publicTag")})
                        </span>
                                                ) : (
                                                    <span className="flex text-red-500">
                          ({t("edit.fileUploadAndComments.privateTag")})
                        </span>
                                                )}
                                            </div>
                                            <div>
                                                {comment?.date &&
                                                    format(
                                                        new Date(comment?.date || ""),
                                                        "dd '-' MM '-' yyyy HH:mm:ss",
                                                        {
                                                            locale: es,
                                                        },
                                                    )}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex">
                                            <div className="w-full">
                                                <h4>{comment.title}</h4>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizedCommentText,
                                                    }}
                                                />
                                            </div>
                                            {isAdmin && loggedInUser?.roles?.includes("superadmin") && (
                                                <div className="flex items-center justify-end">
                                                    <Button
                                                        className="bg-transparent rounded-full hover:bg-red-300"
                                                        onClick={() =>
                                                            handleDeleteComment(
                                                                comment.id,
                                                                communication.id,
                                                            )
                                                        }
                                                    >
                                                        <Trash className="w-5 h-5 text-red-500"/>
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                ) : (
                    <h4 className="ml-5">{t("edit.fileUploadAndComments.noComments")}</h4>
                )}
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
                <h3 className="mb-2">{t("edit.fileUploadAndComments.writeComment")}</h3>
                <Input
                    className="mb-2"
                    placeholder={t("edit.fileUploadAndComments.titlePlaceholder")}
                    {...register("title", {required: "Title is required"})}
                />
                {errors.title && (
                    <p className="text-red-500 mb-6">{errors.title.message}</p>
                )}
                <Textarea
                    className="mb-2"
                    placeholder={t("edit.fileUploadAndComments.commentPlaceholder")}
                    {...register("text", {
                        required: "Comment is required",
                    })}
                />
                {errors.text && (
                    <p className="text-red-500 mb-6">{errors.text.message}</p>
                )}
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (<div className="loader"/>)
                        : t("buttonTexts.sendComment")}
                </Button>
            </form>

            {/* Botones de cambio de estado */}
            {isAdmin && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold mb-4">{t("edit.editForm.changeState")}</h3>
                    <div className="flex flex-wrap gap-3">
                        {states
                            .filter((state) => state.type === communication?.type)
                            .map((state) => {
                                const isCurrentState = state.id === communication?.state?.id;
                                const stateName = state.names?.find(
                                    (name) => name.language === t.language || "es"
                                )?.text || state.names?.[0]?.text || "Estado";
                                
                                return (
                                    <Button
                                        key={state.id}
                                        onClick={() => handleStateChange(state.id)}
                                        disabled={isCurrentState || isChangingState}
                                        className={`px-4 py-2 min-w-[140px] ${
                                            isCurrentState 
                                                ? "opacity-100 cursor-not-allowed ring-4 ring-offset-2" 
                                                : "hover:opacity-80"
                                        }`}
                                        style={{
                                            backgroundColor: state.color,
                                            color: "white",
                                            border: isCurrentState ? `3px solid ${state.color}` : "none",
                                            boxShadow: isCurrentState ? `0 0 0 3px white, 0 0 0 6px ${state.color}` : "none"
                                        }}
                                    >
                                        {stateName}
                                    </Button>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default FileUploadAndComments;
